import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Field, bLabel } from "../scouting/parts";
import { useAuth } from "../../context/AuthContext";
import { X, SprayCan, ClipboardCheck, Beaker, ArrowLeft, FileText, ChevronRight, Ban } from "lucide-react";

const GROUPS = ["SPRAY NOW", "SPRAY 1-2 DAYS", "SPRAY THIS WEEK", "SCOUT FIRST", "MONITOR", "INTERVAL CAN BE EXTENDED", "HOLD"];
const GSTYLE = {
  "SPRAY NOW": "bg-red-600 text-white", "SPRAY 1-2 DAYS": "bg-orange-500 text-white",
  "SPRAY THIS WEEK": "bg-amber-500 text-white", "SCOUT FIRST": "bg-sky-600 text-white",
  MONITOR: "bg-stone-400 text-white", "INTERVAL CAN BE EXTENDED": "bg-[#7C9673] text-white", HOLD: "bg-[#1F3D34] text-white",
};

export default function SprayPlanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "supervisor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState([]);
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState({});
  const [recordFor, setRecordFor] = useState(null);

  const load = async () => {
    const [{ data: plan }, { data: recommended }] = await Promise.all([
      api.get("/pest/planner"),
      api.get("/pest/spray-orders?status=recommended").catch(() => ({ data: [] })),
    ]);
    setData(plan);
    setRecs(recommended);
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 font-bold uppercase text-sm">Building weekly spray plan…</div>;

  const dismissGroup = async (g) => {
    if (!window.confirm(`Delete all ${g.orders.length} recommendation(s) for ${g.vineyard_name}? This removes them from the plan.`)) return;
    for (const o of g.orders) await api.delete(`/pest/spray-orders/${o.id}`);
    load();
  };

  const deleteRec = async (o) => {
    if (!window.confirm(`Delete recommendation ${o.wo_number}?`)) return;
    await api.delete(`/pest/spray-orders/${o.id}`);
    load();
  };

  const recGroups = Object.values(recs.reduce((acc, o) => {
    const k = o.ranch_number;
    if (!acc[k]) acc[k] = { ranch_number: k, vineyard_name: o.vineyard_name, orders: [] };
    acc[k].orders.push(o);
    return acc;
  }, {}));

  const toggle = (b) => setSel((s) => (s.includes(b.block_id) ? s.filter((x) => x !== b.block_id) : [...s, b.block_id]));
  const selected = data.blocks.filter((b) => sel.includes(b.block_id));
  const selAcres = Math.round(selected.reduce((s, b) => s + (b.acres || 0), 0) * 10) / 10;
  const sameRanch = new Set(selected.map((b) => b.ranch_number)).size === 1;

  return (
    <div className="space-y-5" data-testid="spray-planner">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <button onClick={() => navigate("/pest")} className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1 mb-2"><ArrowLeft size={13} /> Pest &amp; Disease</button>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Weekly Spray Plan</h1>
          <p className="text-stone-500 text-sm mt-1">Decision support only — manager approval required; the pesticide label is controlling.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/pest/orders")} data-testid="spray-orders-link" className="h-12 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><FileText size={15} /> Spray Orders</button>
          <button onClick={() => navigate("/pest/materials")} className="h-12 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><Beaker size={15} /> Approved Materials</button>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="sticky top-2 z-40 bg-[#1F3D34] text-white p-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-bold">{sel.length} blocks · {selAcres} acres {!sameRanch && "· ⚠ select blocks from one vineyard"}</span>
          <button data-testid="create-spray-order-btn" disabled={!sameRanch} onClick={() => navigate("/pest/orders/new", { state: { blocks: selected } })}
            className="h-11 px-5 bg-white text-[#1F3D34] font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40"><SprayCan size={15} /> Create Spray Work Order</button>
        </div>
      )}

      {recGroups.length > 0 && (
        <div data-testid="recommended-orders-section">
          <div className="inline-block px-3 py-1 text-xs font-bold uppercase mb-2 bg-amber-500 text-white">
            RECOMMENDED SPRAYS · {recGroups.length} vineyard{recGroups.length === 1 ? "" : "s"} · {Math.round(recGroups.reduce((s, g) => s + g.orders.reduce((x, o) => x + (o.treated_acres || 0), 0), 0) * 10) / 10} ac
          </div>
          <div className="space-y-2">
            {recGroups.map((g) => {
              const targets = [...new Set(g.orders.map((o) => o.target).filter(Boolean))];
              const blockLabels = [...new Set(g.orders.flatMap((o) => (o.blocks || []).map((b) => bLabel(b.block))))];
              const mats = [...new Set(g.orders.flatMap((o) => (o.recommended_materials || []).map((m) => m.product)))];
              const acres = Math.round(g.orders.reduce((x, o) => x + (o.treated_acres || 0), 0) * 10) / 10;
              return (
                <div key={g.ranch_number} data-testid={`rec-group-${g.ranch_number}`} className="bg-white border border-stone-200 p-3 flex flex-wrap gap-3 items-center">
                  <SprayCan size={18} className="text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">{g.vineyard_name} <span className="text-stone-400 font-normal text-xs">{blockLabels.join(", ")} · {acres} ac</span>
                      <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-500 text-white">RECOMMENDED</span>
                    </div>
                    <div className="text-xs text-stone-600 flex flex-wrap gap-x-3">
                      <span>Target: <b>{targets.join(" + ")}</b></span>
                      <span>Source: <b>scouting findings</b></span>
                      {mats.length > 0 && <span>Suggested: <b>{mats.slice(0, 3).join(", ")}{mats.length > 3 ? "…" : ""}</b></span>}
                    </div>
                    <details className="text-xs text-stone-500 mt-1"><summary className="cursor-pointer font-semibold">Why?</summary>
                      <ul className="list-inside space-y-0.5">
                        {g.orders.map((o) => (
                          <li key={o.id} className="flex items-center gap-2">
                            <span>• {o.wo_number}: {o.target} on {(o.blocks || []).map((b) => bLabel(b.block)).join(", ")}{o.reason ? ` — ${o.reason}` : ""}</span>
                            {isManager && (
                              <button data-testid={`rec-delete-${o.wo_number}`} onClick={(e) => { e.preventDefault(); deleteRec(o); }}
                                className="text-red-700 font-bold underline text-[10px] uppercase">Delete</button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                  {isManager && (
                    <button data-testid={`rec-dismiss-${g.ranch_number}`} onClick={() => dismissGroup(g)}
                      className="h-9 px-3 text-[10px] font-bold uppercase border border-stone-300 text-red-700 flex items-center gap-1"><Ban size={13} /> Delete</button>
                  )}
                  <button data-testid={`rec-review-${g.ranch_number}`}
                    onClick={() => navigate("/pest/orders/new", { state: { ranch_number: g.ranch_number, rec: { ids: g.orders.map((o) => o.id), targets, block_ids: [...new Set(g.orders.flatMap((o) => o.block_ids || []))] } } })}
                    className="h-9 px-3 text-[10px] font-bold uppercase bg-[#1F3D34] text-white flex items-center gap-1">
                    Select Blocks &amp; Build Order <ChevronRight size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {GROUPS.map((g) => {
        const rows = data.blocks.filter((b) => b.status === g);
        if (!rows.length) return null;
        const byRanch = Object.values(rows.reduce((acc, b) => {
          if (!acc[b.ranch_number]) acc[b.ranch_number] = { ranch_number: b.ranch_number, vineyard_name: b.vineyard_name, blocks: [] };
          acc[b.ranch_number].blocks.push(b);
          return acc;
        }, {}));
        return (
          <div key={g}>
            <div className={`inline-block px-3 py-1 text-xs font-bold uppercase mb-2 ${GSTYLE[g]}`}>{g} · {byRanch.length} vineyard{byRanch.length === 1 ? "" : "s"} · {rows.length} blocks · {Math.round(rows.reduce((s, b) => s + (b.acres || 0), 0) * 10) / 10} ac</div>
            <div className="space-y-2">
              {byRanch.map((rg) => {
                const key = `${g}|${rg.ranch_number}`;
                const isOpen = !!open[key];
                const allSel = rg.blocks.every((b) => sel.includes(b.block_id));
                const someSel = rg.blocks.some((b) => sel.includes(b.block_id));
                const acres = Math.round(rg.blocks.reduce((s, b) => s + (b.acres || 0), 0) * 10) / 10;
                const maxPm = Math.max(...rg.blocks.map((b) => b.pm_index ?? -1));
                const pmBlock = rg.blocks.find((b) => (b.pm_index ?? -1) === maxPm);
                const botRank = { HIGH: 3, MODERATE: 2, LOW: 1 };
                const worstBot = rg.blocks.map((b) => b.botrytis.level).sort((a, b2) => (botRank[b2] || 0) - (botRank[a] || 0))[0];
                const reiCount = rg.blocks.filter((b) => b.rei_active).length;
                const lastApp = rg.blocks.map((b) => b.last_app).filter(Boolean).sort((a, b2) => (b2.date || "").localeCompare(a.date || ""))[0];
                const toggleAll = () => setSel((s) => allSel
                  ? s.filter((x) => !rg.blocks.some((b) => b.block_id === x))
                  : [...new Set([...s, ...rg.blocks.map((b) => b.block_id)])]);
                return (
                  <div key={key} data-testid={`plan-ranch-${g.replace(/\s+/g, "_")}-${rg.ranch_number}`} className={`bg-white border ${someSel ? "border-[#1F3D34] border-2" : "border-stone-200"}`}>
                    <div className="p-3 flex flex-wrap gap-3 items-center">
                      <input type="checkbox" checked={allSel} ref={(el) => { if (el) el.indeterminate = someSel && !allSel; }} onChange={toggleAll} className="w-5 h-5 accent-[#1F3D34]" data-testid={`plan-ranch-check-${rg.ranch_number}`} />
                      <button type="button" onClick={() => setOpen((s) => ({ ...s, [key]: !isOpen }))} className="min-w-0 flex-1 text-left" data-testid={`plan-ranch-toggle-${g.replace(/\s+/g, "_")}-${rg.ranch_number}`}>
                        <div className="font-bold">{rg.vineyard_name} <span className="text-stone-400 font-normal text-xs">{rg.blocks.length} block{rg.blocks.length === 1 ? "" : "s"} · {acres} ac</span>
                          {reiCount > 0 && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-100 text-red-700">REI ACTIVE · {reiCount}</span>}
                        </div>
                        <div className="text-xs text-stone-600 flex flex-wrap gap-x-3">
                          <span>Powdery: <b>{maxPm >= 0 ? `${maxPm} ${pmBlock?.pm_level || ""}` : "—"}</b></span>
                          <span>Botrytis: <b>{worstBot || "—"}</b></span>
                          <span>Blocks: <b>{rg.blocks.map((b) => bLabel(b.block)).join(", ")}</b></span>
                          <span>Last spray: <b>{lastApp ? `${lastApp.date} · ${lastApp.product}` : "none recorded"}</b></span>
                        </div>
                      </button>
                      <button type="button" onClick={() => setOpen((s) => ({ ...s, [key]: !isOpen }))}
                        className="h-9 px-3 text-[10px] font-bold uppercase border border-stone-300 flex items-center gap-1" data-testid={`plan-ranch-detail-btn-${rg.ranch_number}`}>
                        {isOpen ? "Hide Blocks" : "Block Detail"} <ChevronRight size={13} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="border-t border-stone-200 divide-y divide-stone-100">
                        {rg.blocks.map((b) => (
                          <div key={b.block_id} data-testid={`plan-block-${b.block_id}`} className="p-3 pl-10 flex flex-wrap gap-3 items-center">
                            <input type="checkbox" checked={sel.includes(b.block_id)} onChange={() => toggle(b)} className="w-5 h-5 accent-[#1F3D34]" data-testid={`plan-check-${b.block_id}`} />
                            <div className="min-w-0 flex-1">
                              <div className="font-bold">{bLabel(b.block)} <span className="text-stone-400 font-normal text-xs">{b.variety} · {b.acres} ac{b.phenology ? ` · ${b.phenology}` : ""}</span>
                                {b.rei_active && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-100 text-red-700">REI ACTIVE</span>}
                              </div>
                              <div className="text-xs text-stone-600 flex flex-wrap gap-x-3">
                                <span>Powdery: <b>{b.pm_index ?? "—"} {b.pm_level || ""}</b></span>
                                <span>Botrytis: <b>{b.botrytis.level}</b>{b.botrytis.estimated && <span className="text-amber-600"> (est.)</span>}</span>
                                <span>OLR: <b>{b.olr.dd != null ? `${b.olr.dd} DD` : b.olr.status}</b></span>
                                <span>Last spray: <b>{b.last_app ? `${b.last_app.date} · ${b.last_app.product}${b.last_app.frac_group ? ` (FRAC ${b.last_app.frac_group})` : ""}` : "none recorded"}</b></span>
                                {b.days_remaining != null && <span>Interval: <b>{b.days_since}/{b.interval_days}d ({Math.max(b.days_remaining, 0)} remaining)</b></span>}
                              </div>
                              <details className="text-xs text-stone-500 mt-1"><summary className="cursor-pointer font-semibold">Why?</summary>
                                <ul className="list-disc list-inside">{b.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                                {b.frac_history.length > 0 && <div className="mt-1">FRAC history: {b.frac_history.map((f) => `${f.date} ${f.frac_group || "?"}`).join(" → ")}</div>}
                              </details>
                            </div>
                            <button onClick={() => setRecordFor(b)} className="h-9 px-3 text-[10px] font-bold uppercase border border-stone-300 flex items-center gap-1" data-testid={`record-app-${b.block_id}`}><ClipboardCheck size={13} /> Record Application</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {data.blocks.length === 0 && <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm">Configure vineyard weather sources on the Pest &amp; Disease page first</div>}

      {recordFor && <RecordModal b={recordFor} onClose={() => setRecordFor(null)} onDone={() => { setRecordFor(null); load(); }} />}
    </div>
  );
}

function RecordModal({ b, onClose, onDone }) {
  const [materials, setMaterials] = useState([]);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), material_id: "", product: "", rate: "", rate_unit: "", gpa: "", applicator: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/materials").then(({ data }) => setMaterials(data)); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const m = materials.find((x) => x.id === f.material_id);
      await api.post("/pest/applications", {
        ranch_number: b.ranch_number, block_ids: [b.block_id], date: f.date,
        product: m ? m.product : f.product, material_id: f.material_id || null,
        rate: f.rate ? Number(f.rate) : null, rate_unit: f.rate_unit || m?.rate_unit || null,
        gpa: f.gpa ? Number(f.gpa) : null, target: "Powdery mildew", applicator: f.applicator || null, notes: f.notes || null });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
      <div className="bg-white border border-stone-200 w-full max-w-md p-6 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="record-app-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Record Actual Application — {bLabel(b.block)}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><input type="date" data-testid="app-date-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="Approved Material">
            <select data-testid="app-material-select" value={f.material_id} onChange={(e) => setF({ ...f, material_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              <option value="">— Other (type below) —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.product}</option>)}
            </select>
          </Field>
        </div>
        {!f.material_id && <Field label="Product"><input data-testid="app-product-input" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Rate"><input value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="Unit"><input value={f.rate_unit} onChange={(e) => setF({ ...f, rate_unit: e.target.value })} placeholder="lb/ac" className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="GPA"><input value={f.gpa} onChange={(e) => setF({ ...f, gpa: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>
        <Field label="Applicator"><input value={f.applicator} onChange={(e) => setF({ ...f, applicator: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Notes"><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <button data-testid="app-save-btn" onClick={save} disabled={busy || (!f.material_id && !f.product)} className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">
          {busy ? "Saving…" : "Save Verified Application"}
        </button>
        <div className="text-[10px] text-stone-500">Becomes the source of truth for REI, spray intervals, FRAC rotation and application history.</div>
      </div>
    </div>
  );
}
